import { api, apiBackground } from './api'

/** LOT-LIGNES : politique de sélection du numéro sortant du tenant. */
export type OutboundNumberPolicy =
  | 'FIXED_DEFAULT'
  | 'ROTATE_LEAST_USED'
  | 'PER_AGENT'

/** #157 : mode de sonnerie des appels entrants du tenant. */
export type InboundRingMode =
  | 'TEAM_BROADCAST'
  | 'LAST_AGENT_FIRST'
  | 'LAST_AGENT_ONLY'

export interface Tenant {
  id: string
  name: string
  slug?: string | null
  canMakeCalls?: boolean
  maxContacts?: number | null
  maxCampaigns?: number | null
  maxUsers?: number | null
  createdAt: string
  updatedAt: string
  // Backend retourne directement les counts, pas _count
  userCount?: number
  campaignCount?: number
  contactCount?: number
  // LOT-LIGNES : politique de numéro sortant (défaut serveur FIXED_DEFAULT)
  outboundNumberPolicy?: OutboundNumberPolicy
  // LOT-SUNSET-0 (S0-E) : enregistrement des appels (réglage par tenant)
  recordCalls?: boolean
  // #208 : option « import intelligent » (par client, éteinte par défaut ;
  // facturation à définir — réglée par le super-admin dans la fiche du tenant)
  smartImportEnabled?: boolean
  // #157 : plan de sonnerie des entrants (défaut serveur TEAM_BROADCAST, 12 s)
  inboundRingMode?: InboundRingMode
  lastAgentFirstSeconds?: number
  // LOT-TRANSCRIPTION : transcription + récap IA des appels ENREGISTRÉS (option
  // par client, éteinte par défaut ; exige `recordCalls` — 400 RECORD_CALLS_REQUIRED
  // sinon) et plafond de minutes d'audio transcrites par jour (UTC).
  transcribeCalls?: boolean
  transcriptionDailyMinutesCap?: number
}

export interface TenantStats {
  totalUsers: number
  totalCampaigns: number
  totalContacts: number
  totalCalls: number
}

class TenantsService {
  private static readonly BASE_PATH = '/tenants'

  /**
   * Get all tenants (System Admin only) - no global loader
   */
  static async getAll(): Promise<Tenant[]> {
    const response = await apiBackground.get<Tenant[]>(TenantsService.BASE_PATH)
    return response.data
  }

  /**
   * Get tenant by ID
   */
  static async getById(id: string): Promise<Tenant> {
    const response = await api.get<Tenant>(`${TenantsService.BASE_PATH}/${id}`)
    return response.data
  }

  /**
   * Get tenant stats
   */
  static async getStats(id: string): Promise<TenantStats> {
    const response = await api.get<TenantStats>(`${TenantsService.BASE_PATH}/${id}/stats`)
    return response.data
  }

  /**
   * Create a new tenant (System Admin only)
   */
  static async create(data: {
    name: string
    slug?: string
    canMakeCalls?: boolean
    maxUsers?: number
    maxContacts?: number
    maxCampaigns?: number
    adminEmail?: string
    adminPassword?: string
    agentEmail?: string
    agentPassword?: string
  }): Promise<Tenant> {
    const response = await api.post<Tenant>(TenantsService.BASE_PATH, data)
    return response.data
  }

  /**
   * Update tenant
   */
  static async update(id: string, data: Partial<Tenant>): Promise<Tenant> {
    const response = await api.patch<Tenant>(`${TenantsService.BASE_PATH}/${id}`, data)
    return response.data
  }

  /**
   * Delete tenant
   */
  static async delete(id: string): Promise<void> {
    await api.delete(`${TenantsService.BASE_PATH}/${id}`)
  }

  /**
   * #191 — plan de sonnerie de MON tenant (ADMIN du tenant courant).
   * Le tenant est celui du JWT : aucun id ne part dans l'URL ni dans le corps.
   */
  static async getMyInboundRing(): Promise<InboundRingSettings> {
    const response = await api.get<InboundRingSettings>(
      `${TenantsService.BASE_PATH}/me/inbound-ring`
    )
    return response.data
  }

  /**
   * #208 — les OPTIONS de MON tenant (tout utilisateur authentifié ; le
   * tenant est celui du JWT), lues en base à chaque appel : l'écran montre la
   * carte « Import intelligent » et ouvre `/import/intelligent` sur la foi de
   * `smartImportEnabled` ; l'API `/contact-import/*` relit le même drapeau de
   * son côté. Même route que la pause du plateau (`canMakeCalls`, lue par
   * `telephony/api.ts getTenantOptions`) : une seule, deux lecteurs.
   */
  static async getMyOptions(): Promise<TenantOptions> {
    const response = await api.get<TenantOptions>(`${TenantsService.BASE_PATH}/me/options`)
    return response.data
  }

  /** #191 — mise à jour restreinte aux deux réglages du plan de sonnerie. */
  static async updateMyInboundRing(patch: InboundRingPatch): Promise<InboundRingSettings> {
    const response = await api.patch<InboundRingSettings>(
      `${TenantsService.BASE_PATH}/me/inbound-ring`,
      patch
    )
    return response.data
  }

  /**
   * LOT-TRANSCRIPTION — option « transcription » de MON tenant (ADMIN du
   * tenant courant, tenant du JWT). `recordCalls` est livré en LECTURE : sans
   * enregistrement, l'option n'a rien à transcrire et le serveur la refuse.
   */
  static async getMyTranscription(): Promise<TranscriptionSettings> {
    const response = await api.get<TranscriptionSettings>(
      `${TenantsService.BASE_PATH}/me/transcription`
    )
    return response.data
  }

  /**
   * LOT-TRANSCRIPTION — mise à jour restreinte aux deux réglages de l'option.
   * Requête de FOND (`X-Background-Request`) : l'intercepteur global de `api.ts`
   * (gelé) toaste le message serveur brut de tout 4xx — ici le composant parle
   * (`RECORD_CALLS_REQUIRED` en clair), un seul toast (recette PR #221, D2).
   */
  static async updateMyTranscription(patch: TranscriptionPatch): Promise<TranscriptionSettings> {
    const response = await apiBackground.patch(`${TenantsService.BASE_PATH}/me/transcription`, patch)
    return response.data as TranscriptionSettings
  }

  /** LOT-TRANSCRIPTION — même réglage depuis la fiche client (super-admin), même requête de fond. */
  static async updateTranscription(id: string, patch: TranscriptionPatch): Promise<Tenant> {
    const response = await apiBackground.patch(`${TenantsService.BASE_PATH}/${id}`, patch)
    return response.data as Tenant
  }

  /**
   * PAUSE-PLATEAU (super-admin, liste des clients) — met un plateau EN PAUSE
   * ou le RÉACTIVE. Même route que les deux boutons de la fiche client
   * (`PATCH /tenants/:id` avec `canMakeCalls`) : `POST /tenants/:id/enable-calls`
   * existe aussi mais EFFACE les quotas (`maxContacts`/`maxCampaigns`/`maxUsers`
   * remis à null) — inacceptable pour un geste répété plusieurs fois par jour.
   */
  static async setPaused(id: string, paused: boolean): Promise<Tenant> {
    return TenantsService.update(id, { canMakeCalls: !paused })
  }
}

/**
 * Réponse de `GET /tenants/me/options` : les options du tenant courant —
 * PAUSE-PLATEAU (`id`, `canMakeCalls` : false = plateau en pause) et #208
 * (`smartImportEnabled`). Rien d'autre : ni quotas, ni compteurs.
 */
export interface TenantOptions {
  id: string
  canMakeCalls: boolean
  smartImportEnabled: boolean
}

/** #191 — les deux réglages du plan de sonnerie, tels que rendus par « mon tenant ». */
export interface InboundRingSettings {
  id: string
  inboundRingMode: InboundRingMode
  lastAgentFirstSeconds: number
}

/** #191 — corps du PATCH : un des deux champs, ou les deux. */
export type InboundRingPatch = Partial<Pick<InboundRingSettings, 'inboundRingMode' | 'lastAgentFirstSeconds'>>

/** LOT-TRANSCRIPTION — réponse de `GET/PATCH /tenants/me/transcription`. */
export interface TranscriptionSettings {
  id: string
  /** Lecture seule ici : l'option exige l'enregistrement des appels. */
  recordCalls: boolean
  transcribeCalls: boolean
  /** Minutes d'audio transcrites par jour (0..1440). */
  transcriptionDailyMinutesCap: number
}

/** LOT-TRANSCRIPTION — corps du PATCH : un des deux champs, ou les deux. */
export type TranscriptionPatch = Partial<
  Pick<TranscriptionSettings, 'transcribeCalls' | 'transcriptionDailyMinutesCap'>
>

/** Code renvoyé par le serveur (400) quand l'option est demandée sans enregistrement. */
export const RECORD_CALLS_REQUIRED = 'RECORD_CALLS_REQUIRED'

export default TenantsService
